import { __awaiter, __decorate } from "tslib";
import { Connect } from '@genesislcap/foundation-comms';
import { isControl, isStringControl, mapStateToControlProps, rankWith, } from '@jsonforms/core';
import { attr, customElement, DOM, observable, volatile } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { foundationFormStyles as styles } from './form.styles';
import { foundationFormTemplate as template } from './form.template';
import { BooleanControlEntry } from './jsonforms/renderers/BooleanControlRenderer';
import { ConnectedMultiselectControlRendererEntry } from './jsonforms/renderers/ConnectedMultiselectControlRenderer';
import { ConnectedSelectControlRendererEntry } from './jsonforms/renderers/ConnectedSelectControlRenderer';
import { DateControlRendererEntry } from './jsonforms/renderers/DateControlRenderer';
import { EnumControlRendererEntry } from './jsonforms/renderers/EnumControlRenderer';
import { InvisibleControlEntry } from './jsonforms/renderers/InvisibleControlRenderer';
import { LayoutArrayRendererEntry } from './jsonforms/renderers/LayoutArrayRenderer';
import { LayoutCategorizationRendererEntry } from './jsonforms/renderers/LayoutCategorizationRenderer';
import { LayoutGroupRendererEntry } from './jsonforms/renderers/LayoutGroupRenderer';
import { LayoutHorizontalEntry } from './jsonforms/renderers/LayoutHorizontalRenderer';
import { LayoutRendererEntry } from './jsonforms/renderers/LayoutRenderer';
import { LayoutStepperRendererEntry } from './jsonforms/renderers/LayoutStepperRenderer';
import { LayoutVertical2ColumnsEntry } from './jsonforms/renderers/LayoutVertical2ColumnsRenderer';
import { NumberControlRendererEntry } from './jsonforms/renderers/NumberControlRenderer';
import { StringControlRendererTemplate } from './jsonforms/renderers/StringControlRenderer';
import { logger } from './utils';
const stringEntry = {
    renderer: StringControlRendererTemplate,
    tester: rankWith(2, isStringControl),
    mapper: mapStateToControlProps,
};
const fallbackEntry = {
    renderer: StringControlRendererTemplate,
    tester: rankWith(1, isControl),
    mapper: mapStateToControlProps,
};
/** @public */
export const renderers = [
    fallbackEntry,
    stringEntry,
    BooleanControlEntry,
    LayoutArrayRendererEntry,
    LayoutCategorizationRendererEntry,
    LayoutGroupRendererEntry,
    LayoutHorizontalEntry,
    LayoutVertical2ColumnsEntry,
    LayoutRendererEntry,
    LayoutStepperRendererEntry,
    NumberControlRendererEntry,
    ConnectedMultiselectControlRendererEntry,
    EnumControlRendererEntry,
    ConnectedSelectControlRendererEntry,
    DateControlRendererEntry,
    InvisibleControlEntry,
];
/**
 * Foundation form component for automatically generated forms based on json schema
 * obtained from the api, supplied initial data or supplied JSON schema.
 * Allowing customisable form elements using UI schema and set of custom renderers
 * @beta
 */
let Form = class Form extends FoundationElement {
    constructor() {
        super(...arguments);
        /**
         * Name of the design system prefix that will be used in renderers.
         * @public
         */
        this.prefix = 'zero';
        /**
         * Allows to provide set of renderers used by the form. If not provided it will default to text-field inputs
         * @public
         */
        this.renderers = renderers;
        this.errors = [];
        /**
         * Initial data for the form
         * @public
         */
        this.data = {};
    }
    resourceNameChanged() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.jsonSchema = undefined;
            if (this.resourceName) {
                const jsonSchemaResponse = yield this.connect.getJSONSchema(this.resourceName);
                this.jsonSchema = (_b = (_a = jsonSchemaResponse === null || jsonSchemaResponse === void 0 ? void 0 : jsonSchemaResponse.INBOUND) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.DETAILS;
            }
        });
    }
    removeDataProperties() {
        if (!this.jsonSchema || !this.data) {
            return;
        }
        // removes data provided to the form that isn't part of the schema
        const properties = Object.keys(this.jsonSchema.properties);
        let removedPropertiesCount = 0;
        Object.keys(this.data).forEach((key) => {
            if (!properties.includes(key)) {
                delete this.data[key];
                removedPropertiesCount = removedPropertiesCount + 1;
                logger.debug('Removing key ', key, 'from provided data because it is not part of the payload');
            }
        });
        // force update of the data object to trigger error validation
        if (removedPropertiesCount > 0) {
            this.data = Object.assign({}, this.data);
        }
        return removedPropertiesCount > 0;
    }
    /**
     * @internal
     */
    _submit() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.submitting) {
                return;
            }
            if (this.removeDataProperties()) {
                yield DOM.nextUpdate();
            }
            this.submitted = true;
            const payload = Object.assign({}, this.data);
            logger.debug({ payload, errors: this.errors });
            this.$emit('submit', { payload, errors: this.errors });
            if (Object.entries(payload).length !== 0 && this.resourceName) {
                this.submitting = true;
                const formSubmitReq = yield this.connect.commitEvent(this.resourceName, { DETAILS: payload });
                this.submitting = false;
                if (formSubmitReq.ERROR) {
                    this.$emit('submit-failure', { payload, errors: formSubmitReq.ERROR });
                }
                else {
                    this.$emit('submit-success', { payload });
                }
            }
        });
    }
    get isSubmitHidden() {
        if (this.readonly || this.hideSubmit) {
            return false;
        }
        else {
            return true;
        }
    }
    /**
     * @internal
     */
    submitPart(event) {
        this.submitted = true;
    }
    /**
     * @internal
     */
    onChange(event) {
        logger.debug({ event });
        if (event.detail) {
            this.data = event.detail.data;
            this.errors = event.detail.errors;
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.reset();
    }
    /**
     * Reset the form state
     * @public
     */
    reset(clearData = true) {
        this.submitted = false;
        if (clearData) {
            this.data = {};
        }
    }
};
__decorate([
    attr({ attribute: 'design-system-prefix' })
], Form.prototype, "prefix", void 0);
__decorate([
    attr
], Form.prototype, "resourceName", void 0);
__decorate([
    observable
], Form.prototype, "uischema", void 0);
__decorate([
    observable
], Form.prototype, "renderers", void 0);
__decorate([
    observable
], Form.prototype, "jsonSchema", void 0);
__decorate([
    Connect
], Form.prototype, "connect", void 0);
__decorate([
    observable
], Form.prototype, "data", void 0);
__decorate([
    observable
], Form.prototype, "submitting", void 0);
__decorate([
    observable
], Form.prototype, "submitted", void 0);
__decorate([
    attr({ mode: 'boolean' })
], Form.prototype, "readonly", void 0);
__decorate([
    attr({ attribute: 'hide-submit-button', mode: 'boolean' })
], Form.prototype, "hideSubmit", void 0);
__decorate([
    volatile
], Form.prototype, "isSubmitHidden", null);
Form = __decorate([
    customElement({
        name: 'foundation-form',
        template,
        styles,
    })
], Form);
export { Form };
